import { BadgeCardProps } from '@pandiafi/components'

import type { CoreDayReservableArea } from '../../../common/validations/contractDayReservableAreasSchema'

import { todayISO } from 'utils/dateUtils'

export const isNullishStr = (v: string | null): boolean => !v || v === '(null)'

export const normDate = (v: string | null): string | undefined =>
  isNullishStr(v) ? undefined : String(v).slice(0, 10)

export const fmtCurrency = (
  amount: number,
  locale: string,
  currency: string = 'EUR'
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: amount % 1 ? 2 : 0,
    minimumFractionDigits: amount % 1 ? 2 : 0,
  }).format(amount)

export type PricePeriod = {
  price: number
  validFrom?: string | null
  validTo?: string | null
}

export const priceForToday = (
  prices?: PricePeriod[],
  today: string = todayISO()
): number | undefined => {
  if (!prices?.length) return undefined

  const chosen = prices.reduce<PricePeriod | undefined>((acc, p) => {
    const fromISO = normDate(p.validFrom ?? null) ?? '0000-01-01'
    const toISO = normDate(p.validTo ?? null) ?? '9999-12-31'
    const active = fromISO <= today && today <= toISO
    if (!active) return acc
    if (!acc) return p
    const accFrom = normDate(acc?.validFrom ?? null) ?? '0000-01-01'
    return fromISO > accFrom ? p : acc
  }, undefined)

  return chosen?.price
}

export const priceForDate = (
  prices: PricePeriod[] | undefined,
  atISO: string | null
): number | undefined => {
  if (!prices?.length) return undefined
  const at = atISO ? atISO.slice(0, 10) : todayISO()

  const match = prices.find((p) => {
    const fromISO = normDate(p.validFrom ?? null) ?? '0000-01-01'
    const toISO = normDate(p.validTo ?? null) ?? '9999-12-31'
    return fromISO <= at && at <= toISO
  })
  return match?.price
}

const isDateInRange = (
  date: string,
  start: string | null,
  end: string | null
): boolean => {
  const d = date
  const s = start ?? '0000-01-01'
  const e = end ?? '9999-12-31'
  return s <= d && d <= e
}

export type ReservationKind = 'own' | 'other' | 'upcoming-own'

export const getReservationKind = (
  reservation: { kind: string; startDate: string; endDate: string | null },
  today: string = todayISO()
): ReservationKind | null => {
  const { kind, startDate, endDate } = reservation
  const start = normDate(startDate)
  const end = normDate(endDate)

  if (!start) return null

  const isActive = isDateInRange(today, start, end ?? null)
  const isUpcoming = start > today

  if (kind === 'own' && isActive) return 'own'
  if (kind === 'own' && isUpcoming) return 'upcoming-own'
  if (kind === 'other' && (isActive || isUpcoming)) return 'other'

  return null
}

export const mapStatus = (
  area: CoreDayReservableArea
): BadgeCardProps['status'] => {
  const today = todayISO()

  const ownActive = area.reservations.some(
    (r) => getReservationKind(r, today) === 'own'
  )
  if (ownActive) return 'active'

  const ownUpcoming = area.reservations.some(
    (r) => getReservationKind(r, today) === 'upcoming-own'
  )
  if (ownUpcoming) return 'upcoming'

  const otherActive = area.reservations.some(
    (r) => getReservationKind(r, today) === 'other'
  )
  if (otherActive) return 'disabled'

  return 'default'
}
